import { ColumnDef } from "@tanstack/react-table";
import { PriceList } from "@/app/types/pricing";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Eye, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header"; 
import { format } from 'date-fns';

// Custom event dispatchers
export const dispatchPriceListEvent = (eventName: string, detail: PriceList): void => {
  document.dispatchEvent(new CustomEvent(eventName, { detail }));
};

export const columnsPriceList: ColumnDef<PriceList>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => <div className="font-medium">{row.getValue("name")}</div>,
  },
  {
    accessorKey: "description",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
    cell: ({ row }) => <span>{row.getValue("description") || "-"}</span>,
  },
   {
    accessorKey: "applicable_to",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Applicable To" />,
    cell: ({ row }) => <Badge variant="outline" className="capitalize">{row.getValue("applicable_to")}</Badge>,
  },
   {
    accessorKey: "effective_from",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Effective From" />,
    cell: ({ row }) => {
        const date = row.getValue("effective_from") as string | null;
        return <span>{date ? format(new Date(date), "PPP") : "-"}</span>;
    },
  },
  {
    accessorKey: "effective_to",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Effective To" />,
     cell: ({ row }) => {
        const date = row.getValue("effective_to") as string | null;
        return <span>{date ? format(new Date(date), "PPP") : "-"}</span>;
    },
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => (
      <Badge variant={row.getValue("is_active") ? "success" : "outline"}>
        {row.getValue("is_active") ? "Active" : "Inactive"}
      </Badge>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const priceList = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
             <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => dispatchPriceListEvent("view-pricelist-items", priceList)}
            >
              <Eye className="mr-2 h-4 w-4" />
              <span>View Items</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => dispatchPriceListEvent("edit-pricelist", priceList)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              <span>Edit Details</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer text-destructive"
              onClick={() => dispatchPriceListEvent("delete-pricelist", priceList)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Delete List</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

// Define filterable columns if needed (e.g., by status, applicable_to)
export const filterablePriceListColumns = [
  {
    id: "is_active",
    title: "Status",
    options: [
      { value: "true", label: "Active" },
      { value: "false", label: "Inactive" },
    ],
  },
   {
    id: "applicable_to",
    title: "Applicable To",
    options: [
      { value: "customers", label: "Customers" },
      { value: "vendors", label: "Vendors" },
      { value: "all", label: "All" },
    ],
  },
  // Add more filters as required
]; 